// Weer + regenalert per komende draaidag (tool-feedback #46). Eén verzoek naar de engine voor
// alle draaidagen in het voorspellingsvenster; de engine zoekt de callsheet-locatie (of valt
// terug op het kantoor) en vat de MET-voorspelling samen.
import { useEffect, useMemo, useState } from 'react';
import { AI_ENGINE_URL } from './apiConfig';
import { authFetch } from './authHelpers';

export const WEER_VENSTER_DAGEN = 9;

const isoVandaag = (nu = new Date()) => `${nu.getFullYear()}-${String(nu.getMonth() + 1).padStart(2, '0')}-${String(nu.getDate()).padStart(2, '0')}`;
const plusDagen = (iso, n) => { const d = new Date(`${iso}T12:00:00`); d.setDate(d.getDate() + n); return isoVandaag(d); };

/** Draaidagen die binnen het venster beginnen (StartDatum = pure datum): [{ id, datum }], op id. */
export function draaidagenInVenster(draaidagen, nu = new Date()) {
  const van = isoVandaag(nu);
  const tot = plusDagen(van, WEER_VENSTER_DAGEN);
  return (draaidagen || [])
    .filter((d) => d?.id && /^rec/.test(d.id))
    .map((d) => ({ id: d.id, datum: String(d.fields?.StartDatum || '').slice(0, 10) }))
    .filter(({ datum }) => datum && datum >= van && datum <= tot)
    .sort((x, y) => (x.id < y.id ? -1 : 1));
}

/** "Regen verwacht · 4,2 mm tussen 7 en 19 u · 9–14 °C · Zeist" */
export function weerOmschrijving(w) {
  if (!w) return '';
  const mm = Number(w.neerslag_mm || 0).toLocaleString('nl-NL', { maximumFractionDigits: 1 });
  const delen = [
    w.regen ? `Regen verwacht: ${mm} mm tussen 7 en 19 u` : (Number(w.neerslag_mm) > 0 ? `${mm} mm neerslag tussen 7 en 19 u` : 'Droog tussen 7 en 19 u'),
    `${w.temp_min}–${w.temp_max} °C`,
    w.bron === 'kantoor' ? 'locatie onbekend, weer bij kantoor Tilburg' : w.plaats,
  ];
  return delen.filter(Boolean).join(' · ');
}

export const WEER_VERVERS_MS = 30 * 60 * 1000;   // MET ververst per uur; elk half uur opnieuw vragen is ruim

/**
 * { [draaidagId]: weer } — leeg zolang er niets is (of de weerbron niet antwoordt).
 * Alleen weer waarvan de datum nog klopt met de draaidag (verplaatst = weg tot het nieuwe antwoord).
 * Ververst elk half uur; een mislukte poging laat het vorige antwoord staan tot de volgende.
 */
export function useDraaidagWeer(draaidagen) {
  const venster = useMemo(() => draaidagenInVenster(draaidagen), [draaidagen]);
  const sleutel = venster.map(({ id, datum }) => `${id}:${datum}`).join(',');
  const [weer, setWeer] = useState({});
  useEffect(() => {
    if (!sleutel) { setWeer({}); return undefined; }
    let actief = true;
    const ids = sleutel.split(',').map((x) => x.split(':')[0]).join(',');
    // Het weer is een extraatje op de achtergrond: niet meetellen in de laadbalk.
    const haal = () => authFetch(`${AI_ENGINE_URL}/api/v1/weer/draaidagen?ids=${encodeURIComponent(ids)}`, { stil: true })
      .then((r) => { if (!r.ok) throw new Error(`Weer ${r.status}`); return r.json(); })
      .then((d) => { if (actief) setWeer(d?.weer || {}); })
      .catch(() => { /* bewust stil: het weer is een extraatje; volgende ronde opnieuw */ });
    haal();
    const timer = setInterval(haal, WEER_VERVERS_MS);
    return () => { actief = false; clearInterval(timer); };
  }, [sleutel]);
  return useMemo(() => {
    const uit = {};
    venster.forEach(({ id, datum }) => { if (weer[id] && weer[id].datum === datum) uit[id] = weer[id]; });
    return uit;
  }, [venster, weer]);
}
